import * as React from 'react';
import {
  Container, Segment, Header, Grid,
} from 'semantic-ui-react';
import { Bar } from 'react-chartjs-2';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { CommonUser, CommonAthleteStatistics } from '../../types';
import { State } from '../../reducers';
import { UserProfileState } from '../../reducers/userProfile.reducer';
import ActivityPreview from '../../containers/ActivityPreview/ActivityPreview';
import { fetchProfileInfo } from '../../actions/user.actions';
import NotFound from '../../pages/NotFound/NotFound';
import ProfileTopSection from '../../components/Profile/ProfileTopSection/ProfileTopSection';
import SegmentHeader from '../../components/SegmentHeader/SegmentHeader';
import StrengthProgressionChart from '../../components/StrengthProgressionChart/StrengthProgressionChart';
import LoadingSegment from '../../components/LoadingSegment/LoadingSegment';
import { ActivitiesState } from '../../reducers/activities.reducer';
import { getActivities } from '../../actions/activities.actions';
import CalendarHeatmap from '../../components/CalendarHeatmap/CalendarHeatmap';
import { WithTranslation, withTranslation } from 'react-i18next';
import { useResponsive } from '../../utils/useResponsive';
import "./UserProfile.css"

export interface UserProfileProps extends WithTranslation{
    dispatch: Dispatch<any>;
    userId: string;
    userProfile: UserProfileState;
    user: CommonUser;
    activities: ActivitiesState;
    athleteStatistics: CommonAthleteStatistics;
}

interface UserProfile {
    isOwnProfile: boolean;
    hideSections: boolean;
}

function mapStateToProps(state: State) {
  return {
    user: state.authentication.user,
    activities: state.activities,
    userProfile: state.userProfile,
    athleteStatistics: state.userProfile.athleteStatistics,
  };
}

const UserProfileComponent: React.FC<UserProfileProps> = (props) => {
  const { isMobile, isDesktop } = useResponsive();
  const [state, setState] = React.useState<UserProfile>({
    isOwnProfile: false,
    hideSections: true
  });

  const chartOptions = {
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        title: {
          display: true,
          text: 'Percentile',
        },
      },
    },
  };

  React.useEffect(() => {
    setState({ isOwnProfile: false, hideSections: true });
  }, []);

  React.useEffect(() => {
    props.dispatch(fetchProfileInfo(props.userId));
    fetchActivities(props.userId, props.activities);
    setProfileOwnership(Number(props.userId));
  }, [props.userId, props.dispatch]);

  const setProfileOwnership = (id: number) => {
    if (id === props.user.id) {
      setState(prev => ({ ...prev, isOwnProfile: true }));
    } else {
      setState(prev => ({ ...prev, isOwnProfile: false }));
    }
  };

  const fetchActivities = (id: string, activities: ActivitiesState) => {
    const query = { activitiesPerPage: 5 };
    props.dispatch(getActivities(id, query));
  };

  const displayTotalTrainingTime = () => {
    if (typeof props.athleteStatistics === 'undefined') {
      return 'Undefined';
    }
    if (props.athleteStatistics.totalTrainingTime > 3600) {
      return props.t('profileStats.hourCount', {count: toHours(props.athleteStatistics.totalTrainingTime)});
    } 
    if (props.athleteStatistics.totalTrainingTime > 60) {
      return props.t('profileStats.minuteCount', {count: toMinutes(props.athleteStatistics.totalTrainingTime)});
    }
    return props.t('profileStats.secondCount', {count: Math.round(props.athleteStatistics.totalTrainingTime)});
  };

  const toHours = (timeInSecs: number): number => {
    return Math.round(timeInSecs / (60 * 60));
  };

  const toMinutes = (timeInSecs: number): number => {
    return Math.round(timeInSecs / (60));
  };

  const { strengthGraphData, heatmapData } = props.userProfile;

  return (
    <>
      {props.userProfile.failed ? (
        <NotFound />
      ) : (
        <Container>
          <LoadingSegment isLoading={!props.userProfile.received} showLoadingDelay={0}>
            <ProfileTopSection
              user={props.user}
              isOwnProfile={state.isOwnProfile}
              statistics={props.athleteStatistics}
            />
          </LoadingSegment>
          {state.hideSections ? <div /> : (
            <Grid stackable>
              <Grid.Column width={8}>
                <Segment className="with-header">
                  <SegmentHeader>{props.t('profileStats.abilitiesOverview')}</SegmentHeader>
                  <LoadingSegment isLoading={!props.userProfile.received} showLoadingDelay={0}>
                    <Bar height={150} data={props.userProfile.polarChartData} options={chartOptions} />
                  </LoadingSegment>
                </Segment>
              </Grid.Column>
              <Grid.Column width={8}>
                <Segment className="with-header">
                  <SegmentHeader>{props.t("profileStats.strengthProgression")}</SegmentHeader>
                  <LoadingSegment isLoading={!props.userProfile.received} showLoadingDelay={0}>
                    <StrengthProgressionChart strengthGraphData={strengthGraphData} />
                  </LoadingSegment>
                </Segment>
              </Grid.Column>
            </Grid>
          )}
          <Segment className="with-header">
            <SegmentHeader>{props.t("profileStats.activitiesHeader")}</SegmentHeader>
            <LoadingSegment isLoading={!(props.userProfile.received && props.activities.received)} showLoadingDelay={0}>
              <Header>{props.t("profileStats.overview")}</Header>
              {isDesktop && (
                <CalendarHeatmap nbOfMonth={9} heatmapData={heatmapData} />
              )}
              {isMobile && (
                <CalendarHeatmap nbOfMonth={6} heatmapData={heatmapData} />
              )}
              <h4>
                {props.t("profileStats.totalTrainingTime")}:{" "} 
                {displayTotalTrainingTime()}
              </h4>
              <Header>{props.t("activityTable.recent")}</Header>
              <ActivityPreview activities={props.activities} userId={props.userId} />
            </LoadingSegment>
          </Segment>
        </Container>
      )}
    </>
  );
};

export default withTranslation('profile')(connect(mapStateToProps)(UserProfileComponent));
